import React, { CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './MarkdownViewer.css';

export interface MarkdownViewerStyleConfig {
  fontSize?: string;
  lineHeight?: string;
  fontWeight?: number | string;
  color?: string;
  fontFamily?: string;
}

interface MarkdownViewerProps {
  content: string;
  className?: string;
  styleConfig?: MarkdownViewerStyleConfig;
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  content,
  className = '',
  styleConfig,
}) => {
  const { fontSize, lineHeight, fontWeight, color, fontFamily } = styleConfig || {};

  const containerStyle: CSSProperties = {
    padding: styleConfig ? '0' : undefined,
    ...(fontSize && { fontSize }),
    ...(lineHeight && { lineHeight }),
    ...(fontWeight && { fontWeight }),
    ...(color && { color }),
    ...(fontFamily && { fontFamily }),
  };

  const customStyles = styleConfig
    ? {
        p: { margin: '0 0 8px 0', whiteSpace: 'pre-line' as const },
        pLast: { marginBottom: 0 },
        strong: { fontWeight: 700 },
        em: { fontStyle: 'italic' as const },
        list: { margin: '0 0 8px 0', paddingLeft: '24px' },
        li: { marginBottom: '4px' },
      }
    : {};

  return (
    <div className={`markdown-viewer ${className}`} style={containerStyle}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Customize rendering if needed
          p: ({ node, ...props }) => (
            <p style={styleConfig ? customStyles.p : undefined} {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong style={styleConfig ? customStyles.strong : undefined} {...props} />
          ),
          em: ({ node, ...props }) => (
            <em style={styleConfig ? customStyles.em : undefined} {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol style={styleConfig ? customStyles.list : undefined} {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul style={styleConfig ? customStyles.list : undefined} {...props} />
          ),
          li: ({ node, ...props }) => (
            <li style={styleConfig ? customStyles.li : undefined} {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
